import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, LogOut, AlertTriangle, CheckCircle, Clock, Ban, 
  User, MessageSquare, Eye, XCircle, Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

export default function AdminReportsQueue() {
  const navigate = useNavigate();
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // Action modal
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionForm, setActionForm] = useState({ action: '', admin_note: '' });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (currentAdmin) {
      fetchReports();
    }
  }, [statusFilter, typeFilter, offset, currentAdmin]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      setCurrentAdmin(response.data);
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      if (typeFilter) params.append('report_type', typeFilter);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await axios.get(`${BACKEND_URL}/api/admin/reports-queue?${params.toString()}`, {
        withCredentials: true
      });
      setReports(response.data.reports);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAction = (report) => {
    setSelectedReport(report);
    setActionForm({ action: '', admin_note: '' });
    setShowActionModal(true);
  };

  const handleTakeAction = async () => {
    if (!actionForm.action) {
      toast.error('Please select an action');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/reports/${selectedReport.report_id}/action`,
        actionForm,
        { withCredentials: true }
      );
      toast.success('Action taken successfully');
      setShowActionModal(false);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to take action');
    }
  };

  const getReportTypeBadge = (type) => {
    const badges = {
      inappropriate_behavior: 'bg-yellow-500/20 text-yellow-400',
      fraud: 'bg-red-500/20 text-red-400',
      spam: 'bg-orange-500/20 text-orange-400',
      harassment: 'bg-red-500/20 text-red-400',
      other: 'bg-gray-500/20 text-gray-400'
    };
    return badges[type] || badges.other;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-orange-500" />
              <span className="text-xl font-bold text-white">Reports Queue</span>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/dashboard')} className="text-gray-400 hover:text-white text-sm">
                Dashboard
              </button>
              <button onClick={() => navigate('/admin/user-control')} className="text-gray-400 hover:text-white text-sm">
                User Control
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
                className="bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                data-testid="filter-status"
              >
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="all">All</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Report Type</label>
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setOffset(0); }}
                className="bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                data-testid="filter-type"
              >
                <option value="">All Types</option>
                <option value="inappropriate_behavior">Inappropriate Behavior</option>
                <option value="fraud">Fraud</option>
                <option value="spam">Spam</option>
                <option value="harassment">Harassment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Pending</p>
                <p className="text-2xl font-bold text-white">{statusFilter === 'pending' ? total : '-'}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Found</p>
                <p className="text-2xl font-bold text-white">{total}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Reports List */}
        <div className="bg-[#12121a] border border-gray-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
            </div>
          ) : reports.length > 0 ? (
            <>
              <div className="divide-y divide-gray-800">
                {reports.map((report) => (
                  <div key={report.report_id} className="p-4 hover:bg-[#0a0a0f]/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Report Header */}
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs ${getReportTypeBadge(report.report_type)}`}>
                            {report.report_type.replace(/_/g, ' ')}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'
                          }`}>
                            {report.status}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {new Date(report.created_at).toLocaleString()}
                          </span>
                        </div>

                        {/* Report Content */}
                        <p className="text-white font-medium mb-1">{report.reason}</p>
                        {report.description && (
                          <p className="text-gray-400 text-sm mb-3">{report.description}</p>
                        )}

                        {/* Users Info */}
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-400">Reporter:</span>
                            <span className="text-white">{report.reporter?.name || 'Unknown'}</span>
                            <span className="text-gray-500">({report.reporter?.email})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                            <span className="text-gray-400">Reported:</span>
                            <button
                              onClick={() => navigate(`/admin/user/${report.reported_user_id}`)}
                              className="text-orange-400 hover:underline"
                            >
                              {report.reported_user?.name || 'Unknown'}
                            </button>
                            <span className="text-gray-500">({report.reported_user?.email})</span>
                          </div>
                        </div>

                        {/* Resolution Info */}
                        {report.status === 'resolved' && (
                          <div className="mt-3 p-3 bg-[#0a0a0f] rounded-lg">
                            <p className="text-gray-400 text-sm">
                              Resolved by {report.resolved_by_name} • Action: <span className="text-white">{report.resolution_action}</span>
                            </p>
                            {report.admin_note && (
                              <p className="text-gray-300 text-sm mt-1">{report.admin_note}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {report.status === 'pending' && (
                        <div className="ml-4">
                          <button
                            onClick={() => handleOpenAction(report)}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition text-sm"
                            data-testid={`action-report-${report.report_id}`}
                          >
                            Take Action
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800">
                <p className="text-gray-400 text-sm">
                  Showing {offset + 1} - {Math.min(offset + limit, total)} of {total} reports
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3 py-1 bg-[#0a0a0f] border border-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    className="px-3 py-1 bg-[#0a0a0f] border border-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-3" />
              <p className="text-gray-400">No reports found</p>
            </div>
          )}
        </div>
      </main>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="bg-[#12121a] border border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Take Action on Report</DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              {/* Report Summary */}
              <div className="p-3 bg-[#0a0a0f] rounded-lg">
                <p className="text-white font-medium">{selectedReport.reason}</p>
                <p className="text-gray-400 text-sm mt-1">
                  Against: {selectedReport.reported_user?.name} ({selectedReport.reported_user?.email})
                </p>
              </div>

              {/* Action Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Select Action</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'dismiss', label: 'Dismiss', icon: XCircle, color: 'gray' },
                    { value: 'warn', label: 'Warn User', icon: AlertTriangle, color: 'yellow' },
                    { value: 'suspend', label: 'Suspend User', icon: Clock, color: 'orange' },
                    { value: 'ban', label: 'Ban User', icon: Ban, color: 'red' }
                  ].map(({ value, label, icon: Icon, color }) => (
                    <button
                      key={value}
                      onClick={() => setActionForm({ ...actionForm, action: value })}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition ${
                        actionForm.action === value
                          ? `bg-${color}-500/20 border-${color}-500/50 text-${color}-400`
                          : 'bg-[#0a0a0f] border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                      style={{
                        backgroundColor: actionForm.action === value ? `rgb(var(--${color}-500) / 0.2)` : undefined
                      }}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin Note */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Admin Note (optional)</label>
                <textarea
                  value={actionForm.admin_note}
                  onChange={(e) => setActionForm({ ...actionForm, admin_note: e.target.value })}
                  rows={3}
                  className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Add notes about this action..."
                />
              </div>

              {/* Warning */}
              {(actionForm.action === 'suspend' || actionForm.action === 'ban') && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {actionForm.action === 'ban' 
                      ? 'This will permanently ban the user. They will not be able to log in again.'
                      : 'This will temporarily suspend the user. They will not be able to log in until reactivated.'}
                  </p>
                </div>
              )}

              <button
                onClick={handleTakeAction}
                disabled={!actionForm.action}
                className="w-full py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Action
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
